import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../app/AuthContext';
import { ApiRequestError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { BrandMark } from '../../components/ui/BrandMark';

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login({ username, password, rememberMe });
      const from = (location.state as { from?: Location })?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Anmeldung fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-surface-950 bg-glow-radial px-4">
      <div className="relative w-full max-w-sm animate-rise-in">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          <BrandMark className="h-16 w-16" />
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-ivory-100">
              Container<span className="text-accent-400">System</span>
            </h1>
            <p className="mt-1.5 text-sm text-ivory-500">Wiegungs- &amp; Materialverwaltung</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="relative flex flex-col gap-4 rounded-lg border border-surface-600 bg-surface-800 p-6 shadow-card">
          <div className="absolute inset-x-0 top-0 h-1 rounded-t-lg bg-brand-gradient" />
          <Input label="Benutzername" autoComplete="username" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} required />
          <Input
            label="Passwort"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <label className="flex items-center gap-2 text-sm text-ivory-300">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-surface-500 bg-surface-900 accent-accent-500"
            />
            Angemeldet bleiben (30 Tage)
          </label>
          {error && <p className="text-sm text-danger-400">{error}</p>}
          <Button type="submit" loading={loading} className="mt-2 w-full">
            Anmelden
          </Button>
        </form>
      </div>
    </div>
  );
}
